import { useEffect, RefObject } from "react";

/**
 * 팝업/모달 열려있는 동안 배경(메인화면) 스크롤을 잠근다.
 * body/html 오버스크롤 억제만으로는 부족 — 이 앱의 메인 콘텐츠 스크롤은 body 가 아니라
 * 내부 컨테이너(예: HomePage 의 overflow-y-auto div)에서 일어나므로, body 잠금은 그
 * 컨테이너에 전혀 영향을 못 준다(MobileWidgetsPopup 에서 먼저 발견된 문제와 동일).
 * → 팝업 루트(containerRef) 밖 또는 "실제 스크롤 가능한 요소"가 아닌 영역의 touchmove 를
 * document 레벨에서 직접 preventDefault 하여 배경 스크롤 전파 자체를 차단.
 */
export function useBodyScrollLock<T extends HTMLElement>(containerRef: RefObject<T | null>, active: boolean = true) {
  useEffect(() => {
    if (!active) return;
    const prev = {
      overflow: document.body.style.overflow,
      overscroll: document.body.style.overscrollBehavior,
      htmlOverscroll: document.documentElement.style.overscrollBehavior,
    };
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";

    const onTouchMove = (e: TouchEvent) => {
      const root = containerRef.current;
      if (!root) return;
      let el = e.target as HTMLElement | null;
      if (!el || !root.contains(el)) { e.preventDefault(); return; }  // 팝업 밖(배경) → 차단
      while (el && el !== root.parentElement) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 1) return;  // 스크롤 가능한 내부 영역 → 허용
        if (el === root) break;
        el = el.parentElement;
      }
      e.preventDefault();  // 스크롤 불가 영역(빈 여백 등) → 배경 전파 차단
    };
    document.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.overscrollBehavior = prev.overscroll;
      document.documentElement.style.overscrollBehavior = prev.htmlOverscroll;
      document.removeEventListener("touchmove", onTouchMove);
    };
  }, [active, containerRef]);
}
